import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { useRememberedCompany } from '../lib/rememberedCompany';
import type { CompanyReport, Forecast, ProjectReport } from '../lib/types';
import { FORECAST_LABELS } from '../lib/types';
import { PriorityBadge } from '../components/Badges';
import { formatDate, formatDateTime, todayValue } from '../lib/dates';
import { DataTable } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { TaskProgressReport } from './TaskProgressReport';
import { TimeLogReport } from './TimeLogReport';

/** Progress is for everybody; the time log is ours. See `view` below. */
type View = 'progress' | 'tasks' | 'time';

export function ReportsPage() {
  /* A page that IS a table: it gets the window, not the reading column. */
  const { session } = useSession();
  const [companyId, setCompanyId] = useState('');
  const [view, setView] = useState<View>('progress');
  const [report, setReport] = useState<CompanyReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const companies = session?.companies ?? [];
  const company = companies.find((row) => row.id === companyId);
  const { companyId: remembered, remember } = useRememberedCompany(companies);

  /**
   * Whose hours went where is OURS, and this is asked by ROLE rather than by reaching for a
   * capability that happens to line up — the rule written all over `access.ts`. `reporting.read`
   * is held at full by both client roles, so gating on it would have offered a customer a tab
   * showing our team's time per person, and the API would then have answered 403.
   */
  const isClient = company ? company.role === 'client' || company.role === 'client_admin' : false;
  const mayReadTimeLog = !!session?.person.isAdmin || (!!company && !isClient);

  // Opens on the company this person last chose, not on whichever is first alphabetically.
  useEffect(() => {
    if (!companyId && remembered) setCompanyId(remembered);
  }, [companyId, remembered]);

  /**
   * Switching to a company you are only a client of takes the TIME LOG tab away with it.
   * Without this it would vanish while its content stayed on screen, and the content would 403.
   *
   * Guarded on the view as well as the role, so a customer on "By task" is not dragged off it
   * the moment the company changed.
   */
  useEffect(() => {
    if (!mayReadTimeLog && view === 'time') setView('progress');
  }, [mayReadTimeLog, view]);

  useEffect(() => {
    if (!companyId) return;
    setReport(null);
    setError(null);
    api
      .get<CompanyReport>(`/companies/${companyId}/report`)
      .then(setReport)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not build the report'));
  }, [companyId]);

  if (companies.length === 0) {
    return (
      <div className="card">
        <div className="empty">
          <h3>No company access</h3>
          <p>Reporting is per company, and you have not been given access to one yet.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Reporting</div>
          <h1>{view === 'time' ? 'Time log' : 'Progress'}</h1>
          <p className="subtitle">
            {view === 'tasks'
                ? 'Every task in flight and how far along it is, from the stage it has reached. Closed work is left out \u2014 this is about what is still moving.'
                : view === 'time'
                  ? 'Every hour booked for this company, who spent it, and which of it is chargeable. Read it to size the next job \u2014 an estimate comes from who did what and how long it took them \u2014 and export it for whoever raises the invoice.'
                  : 'Where each project stands, how fast it is actually moving, and when it is likely to finish. Projections come from work finished recently, not from a plan.'}
          </p>
        </div>
        <div className="row" style={{ gap: 10 }}>
          {/*
            The tabs are no longer staff-only. "By project" and "By task" are both a customer's
            to read — the second is the one that answers "where is my thing" — and only the time
            log stays ours, because whose hours went where is ours to know.
          */}
          <div className="view-tabs">
            <button
              type="button"
              className={`view-tab ${view === 'progress' ? 'active' : ''}`}
              onClick={() => setView('progress')}
            >
              By project
            </button>
            <button
              type="button"
              className={`view-tab ${view === 'tasks' ? 'active' : ''}`}
              onClick={() => setView('tasks')}
            >
              By task
            </button>
            {mayReadTimeLog && (
              <button
                type="button"
                className={`view-tab ${view === 'time' ? 'active' : ''}`}
                onClick={() => setView('time')}
              >
                Time log
              </button>
            )}
          </div>
          <label className="field" style={{ marginBottom: 0, minWidth: 220 }}>
            <span className="field-label">Company</span>
            <select
              value={companyId}
              onChange={(e) => {
                setCompanyId(e.target.value);
                remember(e.target.value);
              }}
            >
              {companies.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {view === 'tasks' && companyId && <TaskProgressReport companyId={companyId} />}
      {view === 'time' && companyId && <TimeLogReport companyId={companyId} />}

      {view === 'progress' && error && <div className="callout mb-2">{error}</div>}
      {view === 'progress' && !report && !error && <div className="empty">Building the report…</div>}

      {view === 'progress' && report && (
        <>
          <div className="stat-row mb-2">
            <Stat label="Projects" value={report.totals.projects} />
            <Stat label="Complete" value={`${report.totals.percentComplete}%`} />
            <Stat label="Open items" value={report.totals.open} />
            <Stat label="Blocked" value={report.totals.blocked} tone={report.totals.blocked > 0 ? 'accent' : undefined} />
            <Stat label="Overdue" value={report.totals.overdue} tone={report.totals.overdue > 0 ? 'accent' : undefined} />
            <Stat label="Finished / week" value={report.totals.perWeek} />
          </div>

          <div className="card mb-2">
            <div className="card-head">
              <h2>Delivery outlook</h2>
              <span className="dim" style={{ fontSize: 12 }}>
                Rates measured over the last {report.windowDays} days
              </span>
            </div>
            <div className="card-pad row wrap" style={{ gap: 8 }}>
              <ForecastChip forecast="on_track" count={report.forecast.onTrack} />
              <ForecastChip forecast="at_risk" count={report.forecast.atRisk} />
              <ForecastChip forecast="late" count={report.forecast.late} />
              <ForecastChip forecast="unknown" count={report.forecast.unknown} />
            </div>
          </div>

          {report.projects.length === 0 ? (
            <div className="card">
              <div className="empty">
                <h3>Nothing to report yet</h3>
                <p>This company has no projects.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="card mb-2">
                <div className="card-head">
                  <h2>By project</h2>
                </div>
                <div className="card-pad">
                  {/* The shared list surface: sort, filter, choose columns, export. */}
                  <DataTable
                    tableKey="report-projects"
                    exportName="report-by-project"
                    columns={PROJECT_COLUMNS}
                    rows={report.projects}
                    searchPlaceholder="Find a project"
                  />
                </div>
              </div>

              <Timeline projects={report.projects} />

              <p className="dim" style={{ fontSize: 12, marginTop: 14 }}>
                Built {formatDateTime(report.generatedAt)}. A projection is remaining
                work divided by the rate it is being finished at — it is an estimate, and the
                confidence note says how much history it rests on.
              </p>
            </>
          )}
        </>
      )}
    </>
  );
}

/**
 * What the by-project report shows, and what each column MEANS.
 *
 * `value` is the number or date behind the cell, which is what sorting, filtering and the CSV
 * use — a progress bar cannot be sorted and a badge cannot be exported.
 */
const PROJECT_COLUMNS: Column<ProjectReport>[] = [
  {
    key: 'project',
    header: 'Project',
    width: '22%',
    value: (project) => project.name,
    filter: 'text',
    render: (project) => (
      <>
        <Link to={`/projects/${project.id}/board`} className="row" style={{ gap: 8 }}>
          <span className="key">{project.code}</span>
          <span style={{ fontWeight: 600 }}>{project.name}</span>
        </Link>
        <div style={{ marginTop: 3 }}>
          <PriorityBadge priority={project.priority} />
        </div>
      </>
    ),
  },
  {
    key: 'progress',
    header: 'Progress',
    width: '170px',
    value: (project) => project.percentComplete,
    filter: 'none',
    render: (project) => (
      <>
        <div className="row" style={{ gap: 8 }}>
          <div className="progress grow">
            <div className="progress-fill" style={{ width: `${project.percentComplete}%` }} />
          </div>
          <span className="mono">{project.percentComplete}%</span>
        </div>
        <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>
          {project.items.done} of {project.items.total} done
          {project.points ? ` · ${project.points.done}/${project.points.total} pts` : ''}
        </div>
      </>
    ),
  },
  {
    key: 'stages',
    header: 'Where it has got to',
    width: '200px',
    /* Sorted and exported by what is closest to being finished. */
    value: (project) => project.percentPassed,
    filter: 'none',
    render: (project) => <StageBar project={project} />,
  },
  {
    key: 'open',
    header: 'Open',
    align: 'right',
    value: (project) => project.items.open,
    filter: 'none',
    render: (project) => <span className="mono">{project.items.open}</span>,
  },
  {
    key: 'blocked',
    header: 'Blocked',
    align: 'right',
    value: (project) => project.items.blocked,
    filter: 'none',
    render: (project) => (
      <span className="mono" style={project.items.blocked ? { color: 'var(--accent)' } : undefined}>
        {project.items.blocked}
      </span>
    ),
  },
  {
    key: 'overdue',
    header: 'Overdue',
    align: 'right',
    hiddenByDefault: true,
    value: (project) => project.items.overdue,
    filter: 'none',
    render: (project) => <span className="mono">{project.items.overdue}</span>,
  },
  {
    key: 'perWeek',
    header: 'Per week',
    align: 'right',
    value: (project) => project.throughput.perWeek,
    filter: 'none',
    render: (project) => <span className="mono">{project.throughput.perWeek}</span>,
  },
  {
    key: 'target',
    header: 'Target',
    value: (project) => project.targetDate,
    filter: 'none',
    render: (project) => (
      <span className="mono">
        {project.targetDate ? formatDate(project.targetDate) : <span className="dim">—</span>}
        {project.slippageDays !== null && project.slippageDays > 0 && (
          <div className="badge badge-warn" style={{ marginTop: 3 }}>
            +{project.slippageDays}d on baseline
          </div>
        )}
      </span>
    ),
  },
  {
    key: 'projected',
    header: 'Projected',
    value: (project) => project.projectedFinish,
    filter: 'none',
    render: (project) => (
      <span className="mono" title={project.projectionBasis}>
        {project.projectedFinish ? formatDate(project.projectedFinish) : <span className="dim">—</span>}
        {project.projectedFinish && (
          <div className="dim" style={{ fontSize: 10 }}>
            {project.projectionConfidence} confidence
          </div>
        )}
      </span>
    ),
  },
  {
    key: 'outlook',
    header: 'Outlook',
    value: (project) => project.forecast,
    filter: 'choice',
    render: (project) => <ForecastBadge forecast={project.forecast} />,
  },
];

/**
 * Where the open work sits, as one bar.
 *
 * FIVE NUMBERS IN A ROW OF A TABLE IS NOT AN ANSWER. "What is actually happening" is a shape
 * question — how much is through, how much is being checked, how much has not been started —
 * and a stacked bar says it in the width of a column where five columns of counts would not
 * fit at all.
 *
 * Each band carries its own count in the tooltip rather than a legend, because a legend for
 * five colours costs more room than the bar.
 */
function StageBar({ project }: { project: ProjectReport }) {
  const { stages } = project;
  const total = project.items.total;

  if (total === 0) return <span className="dim">No work yet</span>;

  const bands: Array<{ key: string; label: string; count: number }> = [
    { key: 'done', label: 'Done', count: stages.done },
    { key: 'testing', label: 'Testing', count: stages.testing },
    { key: 'developed', label: 'Development done', count: stages.developed },
    { key: 'developing', label: 'Developing', count: stages.developing },
    { key: 'design', label: 'Design', count: stages.design },
    { key: 'research', label: 'Research', count: stages.research },
    { key: 'unsaid', label: 'Not started', count: stages.unsaid },
  ];

  return (
    <>
      <div className="stage-bar" role="img" aria-label={bands.map((b) => `${b.count} ${b.label}`).join(', ')}>
        {bands
          .filter((band) => band.count > 0)
          .map((band) => (
            <span
              key={band.key}
              className={`stage-band is-${band.key}`}
              style={{ width: `${(band.count / total) * 100}%` }}
              title={`${band.count} ${band.label.toLowerCase()}`}
            />
          ))}
      </div>
      <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>
        {project.percentPassed}% passed
        {stages.testing > 0 ? ` · ${stages.testing} in testing` : ''}
      </div>
    </>
  );
}

/** Everything is laid out against one shared horizon so projects can be compared by eye. */
function Timeline({ projects }: { projects: ProjectReport[] }) {
  const dates = projects
    .flatMap((p) => [p.startDate, p.targetDate, p.baselineTargetDate, p.projectedFinish])
    .filter((d): d is string => !!d);
  const today = todayValue();
  if (dates.length === 0) return null;

  const all = [...dates, today].sort();
  const from = new Date(all[0]).getTime();
  const to = new Date(all[all.length - 1]).getTime();
  const span = Math.max(to - from, 86_400_000);
  // Inset the scale so a marker sitting on the first or last date still has room for its
  // label instead of being clipped by the edge of the track.
  const at = (date: string) => 3 + ((new Date(date).getTime() - from) / span) * 94;

  return (
    <div className="card">
      <div className="card-head">
        <h2>Timeline</h2>
        <span className="dim" style={{ fontSize: 12 }}>
          Solid line is the target date, ring is the projection
        </span>
      </div>
      <div className="card-pad">
        {projects.map((project) => {
          const start = project.startDate ?? today;
          const end = project.projectedFinish ?? project.targetDate ?? today;
          const left = Math.min(at(start), at(end));
          const right = Math.max(at(start), at(end));

          return (
            <div key={project.id} className="timeline-row">
              <div className="truncate">
                <span className="key">{project.code}</span>{' '}
                <span style={{ fontWeight: 600 }}>{project.name}</span>
                <div>
                  <ForecastBadge forecast={project.forecast} />
                </div>
              </div>
              <div className="timeline-track">
                <div className="timeline-bar" style={{ left: `${left}%`, width: `${Math.max(right - left, 0.6)}%` }} />
                <div className="timeline-today" style={{ left: `${at(today)}%` }} title={`Today, ${today}`} />
                {project.targetDate && (
                  <>
                    <div className="timeline-marker" style={{ left: `${at(project.targetDate)}%` }} title={`Target ${project.targetDate}`} />
                    <span className="timeline-marker-label" style={{ left: `${at(project.targetDate)}%` }}>
                      {project.targetDate.slice(5)}
                    </span>
                  </>
                )}
                {project.projectedFinish && (
                  <div
                    className="timeline-dot"
                    style={{ left: `${at(project.projectedFinish)}%` }}
                    title={`Projected ${project.projectedFinish} — ${project.projectionBasis}`}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string | number; tone?: 'accent' }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={tone === 'accent' ? { color: 'var(--accent)' } : undefined}>
        {value}
      </div>
    </div>
  );
}

const FORECAST_CLASS: Record<Forecast, string> = {
  on_track: 'badge badge-ok',
  at_risk: 'badge badge-warn',
  late: 'badge badge-accent',
  done: 'badge badge-ok',
  unknown: 'badge',
};

function ForecastBadge({ forecast }: { forecast: Forecast }) {
  return <span className={FORECAST_CLASS[forecast]}>{FORECAST_LABELS[forecast]}</span>;
}

function ForecastChip({ forecast, count }: { forecast: Forecast; count: number }) {
  return (
    <span className={FORECAST_CLASS[forecast]} style={{ opacity: count === 0 ? 0.45 : 1, padding: '4px 11px' }}>
      {count} {FORECAST_LABELS[forecast].toLowerCase()}
    </span>
  );
}
